import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  SunIcon,
  MoonIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import useAuthStore from '../../store/authStore';
import themeStore from '../../store/themeStore';
import useCommandStore from '../../store/commandStore';

function getInitials(user) {
  if (!user) return '?';
  return `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase();
}

export default function Header({ onMenuClick }) {
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = themeStore();
  const { setOpen: openCommand } = useCommandStore();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700 px-4 h-14 flex items-center shrink-0">
      <div className="flex items-center justify-between w-full gap-3">
        {/* Left: hamburger (mobile) */}
        <button
          onClick={onMenuClick}
          className="btn btn-ghost btn-icon md:hidden"
          aria-label="Open menu"
        >
          <Bars3Icon className="w-5 h-5" />
        </button>

        {/* Center: command palette trigger */}
        <button
          onClick={() => openCommand(true)}
          className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-neutral-100 dark:bg-neutral-800
                     text-sm text-neutral-500 dark:text-neutral-400 border border-neutral-200 dark:border-neutral-700
                     hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors min-w-[200px]"
          aria-label="Open command palette"
        >
          <MagnifyingGlassIcon className="w-4 h-4 shrink-0" />
          <span className="flex-1 text-left">Search…</span>
          <kbd className="text-xs bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 px-1.5 py-0.5 rounded font-mono">
            ⌘K
          </kbd>
        </button>

        {/* Right: theme toggle + avatar dropdown */}
        <div className="flex items-center gap-1 ml-auto">
          <button
            onClick={toggleTheme}
            className="btn btn-ghost btn-icon"
            aria-label="Toggle theme"
          >
            {theme === 'dark'
              ? <SunIcon className="w-5 h-5" />
              : <MoonIcon className="w-5 h-5" />
            }
          </button>

          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
              <div className="w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400 flex items-center justify-center text-xs font-semibold shrink-0">
                {getInitials(user)}
              </div>
              <ChevronDownIcon className="w-3.5 h-3.5 text-neutral-400 hidden md:block" />
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-1 w-52 rounded-xl bg-white dark:bg-neutral-800 shadow-lg border border-neutral-200 dark:border-neutral-700 focus:outline-none z-50">
                {/* User info */}
                <div className="px-3 py-2.5 border-b border-neutral-100 dark:border-neutral-700">
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{user?.email}</p>
                </div>

                <div className="py-1">
                  {user?.isPlatformAdmin && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => navigate('/platform-admin')}
                          className={`w-full text-left px-3 py-2 text-sm rounded-lg mx-0.5 transition-colors
                            ${active ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300' : 'text-teal-600 dark:text-teal-400'}`}
                        >
                          Platform Admin
                        </button>
                      )}
                    </Menu.Item>
                  )}
                  {!user?.isPlatformAdmin && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => navigate('/settings')}
                          className={`w-full text-left px-3 py-2 text-sm rounded-lg mx-0.5 transition-colors
                            ${active ? 'bg-neutral-100 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100' : 'text-neutral-700 dark:text-neutral-300'}`}
                        >
                          Settings
                        </button>
                      )}
                    </Menu.Item>
                  )}
                </div>

                <div className="border-t border-neutral-100 dark:border-neutral-700 py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={`w-full text-left px-3 py-2 text-sm rounded-lg mx-0.5 transition-colors
                          ${active ? 'bg-danger-50 dark:bg-danger-900/20 text-danger-700 dark:text-danger-400' : 'text-neutral-700 dark:text-neutral-300'}`}
                      >
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </header>
  );
}
